import { useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import Loading from '@components/UI/Loading'
import { IApplicationState } from '@store'
import {
  fetchPrePurchaseAnalytics,
  PrePurchaseAnalyticsType,
  selectIsFetchingPrePurchaseAnalytics,
  selectPrePurchaseAnalyticsType,
} from '@store/prePurchaseAnalytics'
import { selectSubmissionId } from '@store/router'
import {
  selectIdentityId,
  selectIsSubmissionFromPinterest,
  selectIsSubmissionFromTiktok,
} from '@store/submissions'
import { AnalyticsPrePurchasePinterest } from './Pinterest/Pinterest'
import { AnalyticsPrePurchaseReel } from './Reel/Reel'
import AnalyticsPrePurchasePost from './Post'
import AnalyticsPrePurchaseStory from './Story'
import { AnalyticsPrePurchaseTikTok } from './TikTok'

export const isNumber = (param) => typeof param === 'number'

export interface IInternalProps {
  identityId: number
  submissionId: number
  prePurchaseAnalyticsType: PrePurchaseAnalyticsType
  isFetching: boolean
  fetchPrePurchaseAnalytics: (
    identityId: number,
    submissionId: number,
    type: PrePurchaseAnalyticsType,
  ) => void
  isTikTok: boolean
  isPinterest: boolean
}

export const AnalyticsPrePurchase: React.FC<IInternalProps> = ({
  identityId,
  submissionId,
  prePurchaseAnalyticsType,
  isFetching,
  isTikTok,
  isPinterest,
  fetchPrePurchaseAnalytics: fetchPrePurchaseAnalyticsAction,
}) => {
  useEffect(() => {
    fetchPrePurchaseAnalyticsAction(identityId, submissionId, prePurchaseAnalyticsType)
  }, [identityId, submissionId, prePurchaseAnalyticsType])

  if (isFetching) {
    return <Loading />
  }

  if (isTikTok) {
    return <AnalyticsPrePurchaseTikTok />
  }

  if (isPinterest) {
    return <AnalyticsPrePurchasePinterest />
  }

  if (prePurchaseAnalyticsType === PrePurchaseAnalyticsType.STORY) {
    return <AnalyticsPrePurchaseStory />
  }

  if (prePurchaseAnalyticsType === PrePurchaseAnalyticsType.REEL) {
    return <AnalyticsPrePurchaseReel />
  }

  return <AnalyticsPrePurchasePost />
}

const AnalyticsPrePurchaseConnected: React.FC = () => {
  const match = useRouteMatch()
  const dispatch = useDispatch()
  const submissionId = selectSubmissionId(match)!
  const identityId = useSelector((state: IApplicationState) =>
    selectIdentityId(state, submissionId),
  )
  const prePurchaseAnalyticsType = useSelector((state: IApplicationState) =>
    selectPrePurchaseAnalyticsType(state, submissionId),
  )
  const isFetching = useSelector((state: IApplicationState) =>
    selectIsFetchingPrePurchaseAnalytics(state),
  )
  const isTikTok = useSelector((state: IApplicationState) =>
    selectIsSubmissionFromTiktok(state, submissionId),
  )
  const isPinterest = useSelector((state: IApplicationState) =>
    selectIsSubmissionFromPinterest(state, submissionId),
  )

  return (
    <AnalyticsPrePurchase
      identityId={identityId}
      submissionId={submissionId}
      prePurchaseAnalyticsType={prePurchaseAnalyticsType}
      isFetching={isFetching}
      isTikTok={isTikTok}
      isPinterest={isPinterest}
      fetchPrePurchaseAnalytics={(id, sid, type) =>
        dispatch(fetchPrePurchaseAnalytics(id, sid, type))
      }
    />
  )
}

export default AnalyticsPrePurchaseConnected
